/**
 * ═══════════════════════════════════════════════════════════════════════
 * RealtimeSyncService — Transport Layer (Security-Hardened)
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Architecture:
 *   WebSocket (primary)  →  instant push, ~100ms latency
 *   Reconciliation poll  →  60s safety-net (1 req/min, catches rare WS drops)
 *
 * SECURITY:
 *   • JWT is NEVER sent in URL/query string (logged by proxies/CDNs)
 *   • Auth token sent via post-connect message over encrypted WS channel
 *   • Token auto-refreshed before reconnect via Amplify session
 *   • All inbound events validated against type allowlist
 *   • Payload schema validated before state application
 *   • Tenant ID verified server-side, not trusted from client
 *
 * WebSocket protocol:
 *   → (connect with no token in URL)
 *   → { action: 'authenticate', token: 'JWT...' }
 *   ← { type: 'AUTH_OK' }
 *   → { action: 'subscribe',   tenantId }
 *   ← { type: 'SUBSCRIPTION_CONFIRMED', tenantId }   ← NOW "connected"
 *   ↔ { action: 'ping' } / { type: 'pong' }          ← heartbeat every 30s
 *   ← { type: 'VISIT_CREATED', payload: { ... }, tenantId, timestamp }
 *
 * Key design decisions:
 *   • "Connected" status ONLY after SUBSCRIPTION_CONFIRMED (not AUTH_OK)
 *   • Heartbeat ping/pong every 30s — detects dead connections in <40s
 *   • 60s reconciliation as safety-net (not primary transport)
 *   • Clean reconnect on ANY WS close (clean or unclean)
 *   • WS retries: 10 attempts with exponential backoff, then polling fallback
 *
 * Cost at scale (60s reconciliation):
 *   100 clinics × 5 tabs × 1 req/60s = 8.3 req/s — negligible
 */

import eventBus from './EventBus';
import crossTabSync from './CrossTabSync';
import { requestTokenRefresh, getRefreshPromise } from '../api/apiClient';

// ── Event Types ───────────────────────────────────────────────────────
export const REALTIME_EVENTS = {
  // Visit lifecycle
  VISIT_CREATED: 'VISIT_CREATED',
  VISIT_UPDATED: 'VISIT_UPDATED',
  VISIT_DELETED: 'VISIT_DELETED',
  VISIT_STATUS_CHANGED: 'VISIT_STATUS_CHANGED',

  // Queue operations
  QUEUE_MOVED: 'QUEUE_MOVED',
  QUEUE_STARTED: 'QUEUE_STARTED',
  QUEUE_SKIPPED: 'QUEUE_SKIPPED',
  VISIT_SKIPPED: 'VISIT_SKIPPED',
  VISIT_REQUEUED: 'VISIT_REQUEUED',

  // Doctor/Staff
  DOCTOR_AVAILABILITY_CHANGED: 'DOCTOR_AVAILABILITY_CHANGED',
  DOCTOR_STATUS_CHANGED: 'DOCTOR_STATUS_CHANGED',
  STAFF_UPDATED: 'STAFF_UPDATED',

  // Visit metadata
  VISIT_METADATA_ADDED: 'VISIT_METADATA_ADDED',

  // Appointments
  APPOINTMENT_CREATED: 'APPOINTMENT_CREATED',
  APPOINTMENT_UPDATED: 'APPOINTMENT_UPDATED',
  APPOINTMENT_CANCELLED: 'APPOINTMENT_CANCELLED',
  APPOINTMENT_CHECKED_IN: 'APPOINTMENT_CHECKED_IN',

  // Billing
  BILLING_STATUS_CHANGED: 'BILLING_STATUS_CHANGED',
  VISIT_BILLING_UPDATED: 'VISIT_BILLING_UPDATED',

  // Treatment Plans + Sittings (Phase 1)
  PLAN_CREATED: 'PLAN_CREATED',
  PLAN_STATUS_CHANGED: 'PLAN_STATUS_CHANGED',
  // Phase 2 — multi-item plan dedicated events
  PLAN_ITEM_CONSUMED: 'PLAN_ITEM_CONSUMED',
  PLAN_COMPLETED: 'PLAN_COMPLETED',
  PLAN_CANCELLED: 'PLAN_CANCELLED',
  SITTING_SCHEDULED: 'SITTING_SCHEDULED',
  SITTING_AWAITING_PAYMENT: 'SITTING_AWAITING_PAYMENT',
  SITTING_PAYMENT_CLEARED: 'SITTING_PAYMENT_CLEARED',
  // Phase 3 — strict-plan one-by-one promotion
  SITTING_HELD: 'SITTING_HELD',
  SITTING_READY: 'SITTING_READY',
  SITTING_EXECUTION_STARTED: 'SITTING_EXECUTION_STARTED',
  SITTING_COMPLETED: 'SITTING_COMPLETED',
  SITTING_PENDING_APPROVAL: 'SITTING_PENDING_APPROVAL',
  SITTING_APPROVED: 'SITTING_APPROVED',
  SITTING_REJECTED: 'SITTING_REJECTED',
  SITTING_REASSIGNED: 'SITTING_REASSIGNED',
  // Procedures-on-New-Visits — sitting's live ownership moved to a new visit
  SITTING_VISIT_MOVED: 'SITTING_VISIT_MOVED',
  SITTING_CANCELLED: 'SITTING_CANCELLED',
  SITTING_WALKIN_NOTIFICATION: 'SITTING_WALKIN_NOTIFICATION',
  // Phase 4.0i — procedure-note conversation thread
  SITTING_NOTE_ADDED: 'SITTING_NOTE_ADDED',
  // Phase 1b — handoff + Rx approval
  VISIT_HANDLER_CHANGED: 'VISIT_HANDLER_CHANGED',
  PRESCRIPTION_APPROVED: 'PRESCRIPTION_APPROVED',
  PRESCRIPTION_REJECTED: 'PRESCRIPTION_REJECTED',
  // Phase 6.0 — visit action-pending overlay + co-worker
  VISIT_ACTION_PENDING_SET: 'VISIT_ACTION_PENDING_SET',
  VISIT_ACTION_PENDING_RESOLVED: 'VISIT_ACTION_PENDING_RESOLVED',
  VISIT_DOCTOR_FOLLOWUP_SET: 'VISIT_DOCTOR_FOLLOWUP_SET',
  VISIT_DOCTOR_FOLLOWUP_CLEARED: 'VISIT_DOCTOR_FOLLOWUP_CLEARED',
  VISIT_NOTE_CREATED: 'VISIT_NOTE_CREATED',
  VISIT_BUNDLED_PAYMENT_COLLECTED: 'VISIT_BUNDLED_PAYMENT_COLLECTED',
  VISIT_DOCTOR_CHANGED: 'VISIT_DOCTOR_CHANGED',
  VISIT_COWORKER_ADDED: 'VISIT_COWORKER_ADDED',
  VISIT_COWORKER_REMOVED: 'VISIT_COWORKER_REMOVED',

  // Admission / IPD — bed occupancy state change (C2 live occupancy board).
  // One frame carries the full denormalized bed image (state + version +
  // patient) → the board applies it without any per-bed fetch.
  BED_STATE_CHANGED: 'BED_STATE_CHANGED',

  // Admission / IPD — C4 deposit/charge moved the admission's money state.
  // Emitted post-commit from the WRITTEN admission row; payload
  // { admission_id, balance_paise, deposit_total_paise (NET), deposit_low,
  //   deposit_shortfall_paise } → the desk patches the row without a refetch.
  ADMISSION_BALANCE_CHANGED: 'ADMISSION_BALANCE_CHANGED',

  // Laboratory — a lab order assigned to a technician (or created already
  // assigned, e.g. a walk-in lab visit). Refreshes the technician's queue live.
  LAB_ORDER_ASSIGNED: 'LAB_ORDER_ASSIGNED',
  // A lab order cancelled (by the tech, or cascaded from a visit cancel) —
  // drops it from the technician's queue live.
  LAB_ORDER_CANCELLED: 'LAB_ORDER_CANCELLED',

  // Bulk refresh (polling detected many changes)
  BULK_REFRESH: 'BULK_REFRESH',

  // Connection lifecycle
  CONNECTED: 'REALTIME_CONNECTED',
  DISCONNECTED: 'REALTIME_DISCONNECTED',
  RECONNECTING: 'REALTIME_RECONNECTING',

  // ABDM M1 — patient profile received via Scan & Share gateway callback
  ABHA_SCAN_SHARE: 'abha_scan_share',
  // R56 — a waiting scan was claimed (drops it from every desk's waiting list)
  ABHA_SCAN_SHARE_CLAIMED: 'abha_scan_share_claimed',
};

// ── Security: Allowlisted Event Types ────────────────────────────────
// ONLY these event types are accepted from the WebSocket server.
// Prevents a compromised server from injecting internal events like
// __STATE_SNAPSHOT__ or triggering arbitrary EventBus listeners.
const ALLOWED_REMOTE_EVENT_TYPES = new Set([
  'VISIT_CREATED', 'VISIT_UPDATED', 'VISIT_DELETED', 'VISIT_STATUS_CHANGED',
  'QUEUE_MOVED', 'QUEUE_STARTED', 'QUEUE_SKIPPED',
  'VISIT_SKIPPED', 'VISIT_REQUEUED',
  'DOCTOR_AVAILABILITY_CHANGED', 'DOCTOR_STATUS_CHANGED', 'STAFF_UPDATED',
  'BILLING_STATUS_CHANGED', 'VISIT_BILLING_UPDATED', 'BULK_REFRESH',
  'VISIT_METADATA_ADDED',
  'APPOINTMENT_CREATED', 'APPOINTMENT_UPDATED', 'APPOINTMENT_CANCELLED', 'APPOINTMENT_CHECKED_IN',
  'CALENDAR_BLOCK_CHANGED', 'APPT_IMPACT_CHANGED',
  // Treatment Plans + Sittings (Phase 1)
  'PLAN_CREATED', 'PLAN_STATUS_CHANGED',
  // Phase 2
  'PLAN_ITEM_CONSUMED', 'PLAN_COMPLETED', 'PLAN_CANCELLED',
  'SITTING_SCHEDULED', 'SITTING_AWAITING_PAYMENT', 'SITTING_PAYMENT_CLEARED',
  'SITTING_HELD', 'SITTING_READY',
  'SITTING_EXECUTION_STARTED', 'SITTING_COMPLETED', 'SITTING_PENDING_APPROVAL',
  'SITTING_APPROVED', 'SITTING_REJECTED', 'SITTING_REASSIGNED',
  'SITTING_VISIT_MOVED',
  'SITTING_CANCELLED', 'SITTING_WALKIN_NOTIFICATION',
  // Phase 4.0i — procedure-note conversation thread
  'SITTING_NOTE_ADDED',
  // Phase 1b — handoff + Rx approval
  'VISIT_HANDLER_CHANGED', 'PRESCRIPTION_APPROVED', 'PRESCRIPTION_REJECTED',
  // Phase 6.0 — visit action-pending overlay + co-worker
  'VISIT_ACTION_PENDING_SET', 'VISIT_ACTION_PENDING_RESOLVED',
  'VISIT_DOCTOR_FOLLOWUP_SET', 'VISIT_DOCTOR_FOLLOWUP_CLEARED',
  'VISIT_NOTE_CREATED', 'VISIT_BUNDLED_PAYMENT_COLLECTED',
  'VISIT_DOCTOR_CHANGED', 'VISIT_COWORKER_ADDED', 'VISIT_COWORKER_REMOVED',
  // Laboratory
  'LAB_ORDER_ASSIGNED', 'LAB_ORDER_CANCELLED',
  // Admission / IPD — C2 live occupancy board + C4 live balance
  'BED_STATE_CHANGED',
  'ADMISSION_BALANCE_CHANGED',
  'SUBSCRIPTION_CONFIRMED', 'AUTH_OK', 'AUTH_FAILED',
  // ABDM M1 — Scan & Share uses flat payload (no payload wrapper)
  'abha_scan_share',
  'abha_scan_share_claimed',
]);

// ── Payload size limit (prevents memory DoS) ─────────────────────────
const MAX_PAYLOAD_SIZE = 64 * 1024; // 64 KB — visits are small JSON

// ── Configuration ────────────────────────────────────────────────────
const WS_URL = process.env.REACT_APP_WS_URL || null;
const POLL_INTERVAL = 15_000;          // 15s (polling-only mode, no WS)
const RECONCILE_INTERVAL = 60_000;     // 60s safety-net alongside WS
const RECONNECT_BASE_DELAY = 1_000;    // 1s initial
const RECONNECT_MAX_DELAY = 30_000;    // 30s cap
const MAX_RECONNECT_ATTEMPTS = 10;     // then fall back to polling
const HEARTBEAT_INTERVAL = 30_000;     // 30s ping
const HEARTBEAT_TIMEOUT = 10_000;      // 10s pong deadline

const _debug = process.env.REACT_APP_DEBUG === 'true';
function _log(...args) { if (_debug) console.log('[RT_DEBUG]', ...args); }

class RealtimeSyncService {
  constructor() {
    this._ws = null;
    this._tenantId = null;
    this._authTokenProvider = null; // Function that returns fresh JWT
    this._connected = false;
    this._subscribed = false;        // True ONLY after SUBSCRIPTION_CONFIRMED
    this._authenticated = false;     // WS auth handshake completed
    this._authRejected = false;      // True if server sent AUTH_FAILED
    this._isConnecting = false;      // Re-entrancy guard for _connectWebSocket
    this._reconnectAttempts = 0;
    this._reconnectTimer = null;
    this._reconcileTimer = null;     // 60s reconciliation interval
    this._isDestroyed = false;
    this._mode = null; // 'ws' | 'polling'
    this._visibilityHandler = null;
    this._previousDailyVisits = null; // for delta detection
    this._previousDoctors = null;     // for doctor availability delta detection

    // Heartbeat
    this._heartbeatTimer = null;
    this._pongTimer = null;

    // Throttling
    this._lastReconcileAt = 0;       // cooldown for _doReconcile API calls
    this._subscribedAt = 0;          // when SUBSCRIPTION_CONFIRMED last fired

    // Polling data fetcher — injected by the store layer
    this._pollFetcher = null;
  }

  // ── Public API ─────────────────────────────────────────────────────

  /**
   * Start the realtime service for a specific tenant.
   * @param {Object}   opts
   * @param {string}   opts.tenantId         - organisation ID
   * @param {Function} opts.authTokenProvider - async () => freshJWT (called on each connect/reconnect)
   * @param {Function} opts.pollFetcher       - async () => { dailyVisits, visits }
   */
  connect({ tenantId, authTokenProvider, pollFetcher }) {
    if (this._tenantId === tenantId && this._connected) return;
    this.disconnect();

    this._tenantId = tenantId;
    this._authTokenProvider = authTokenProvider || null;
    this._pollFetcher = pollFetcher;
    this._isDestroyed = false;
    this._reconnectAttempts = 0;

    // Set up cross-tab sync with leader election
    // NOTE: _claimLeadership() fires onLeaderChange(true) synchronously
    // inside crossTabSync.connect(), so we must NOT also check
    // crossTabSync.isLeader afterwards — that would double-start transport
    // and create two parallel polling chains.
    crossTabSync.connect(tenantId, {
      onLeaderChange: (isLeader) => {
        if (isLeader) {
          this._startTransport();
        } else {
          this._stopTransport();
        }
      },
    });

    // Listen for page visibility changes
    this._visibilityHandler = () => this._onVisibilityChange();
    document.addEventListener('visibilitychange', this._visibilityHandler);
  }

  disconnect() {
    this._isDestroyed = true;
    this._stopTransport();
    crossTabSync.disconnect();

    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }

    const prevTenant = this._tenantId;
    this._tenantId = null;
    this._authTokenProvider = null;
    this._connected = false;
    this._subscribed = false;
    this._authenticated = false;
    this._authRejected = false;
    this._previousDailyVisits = null;
    this._previousDoctors = null;

    eventBus.emit({
      type: REALTIME_EVENTS.DISCONNECTED,
      payload: {},
      tenantId: prevTenant,
      source: 'local',
      timestamp: Date.now(),
    });
  }

  get isConnected() {
    return this._connected;
  }

  get mode() {
    return this._mode;
  }

  // ── Transport Selection ────────────────────────────────────────────

  _startTransport() {
    if (this._isDestroyed) return;

    if (WS_URL) {
      this._mode = 'ws';
      _log('Transport → WebSocket:', WS_URL);
      this._connectWebSocket();
      // 60s reconciliation alongside WS (safety-net, not primary)
      this._startReconciliation();
    } else {
      this._mode = 'polling';
      _log('Transport → Polling (no WS_URL)');
      this._startPolling();
    }
  }

  _stopTransport() {
    this._connected = false;
    this._subscribed = false;

    // WebSocket
    if (this._ws) {
      this._ws.onclose = null; // prevent reconnect
      this._ws.close();
      this._ws = null;
    }

    // Heartbeat
    this._stopHeartbeat();

    // Timers
    clearTimeout(this._reconnectTimer);
    this._reconnectTimer = null;
    clearInterval(this._reconcileTimer);
    this._reconcileTimer = null;
  }

  // ═══════════════════════════════════════════════════════════════════
  // WebSocket Transport
  // ═══════════════════════════════════════════════════════════════════

  /**
   * SECURITY: Connect WebSocket WITHOUT token in URL.
   *
   * Token travels inside the TLS tunnel, NOT in the URL.
   * Refreshed via authTokenProvider() on every connect/reconnect.
   */
  async _connectWebSocket() {
    if (this._isDestroyed || !WS_URL) return;

    // Re-entrancy guard — never open a second socket while one is already
    // connecting/open, or while a prior attempt is mid token-fetch. Without
    // this, concurrent callers (_startTransport, _handleWsAuthFailure, the
    // reconnect timer) each create a socket, churning short-lived connections.
    if (this._isConnecting) return;
    if (this._ws && (this._ws.readyState === WebSocket.CONNECTING
      || this._ws.readyState === WebSocket.OPEN)) return;
    this._isConnecting = true;

    try {
      let freshToken = null;
      if (this._authTokenProvider) {
        // Try up to 3 times with delay — Cognito session may not be ready
        // at page load when crossTabSync fires onLeaderChange synchronously.
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            freshToken = await this._authTokenProvider();
            if (freshToken) break;
            // Token returned null — wait a moment and retry
            if (attempt < 3) {
              _log(`Auth token null (attempt ${attempt}/3) — retrying in ${attempt}s...`);
              await new Promise((r) => setTimeout(r, attempt * 1000));
            }
          } catch (err) {
            if (attempt === 3) {
              console.warn('[RT] Token provider failed after 3 attempts:', err.message);
            }
          }
        }
      }

      // If no token after retries, skip WS entirely
      if (!freshToken) {
        this._isConnecting = false;
        console.warn('[RT] No auth token available — falling back to polling');
        this._switchToPollingOnly();
        return;
      }

      this._ws = new WebSocket(WS_URL);
      // The socket now exists (readyState CONNECTING); its readyState guards
      // re-entry from here, so the in-flight flag can be released.
      this._isConnecting = false;
      this._authenticated = false;
      this._subscribed = false;
      const ws = this._ws;

      this._ws.onopen = () => {
        if (this._ws !== ws || this._isDestroyed) {
          try { ws.close(); } catch {}
          return;
        }

        _log('WS open — authenticating...');
        this._ws.send(JSON.stringify({ action: 'authenticate', token: freshToken }));
      };

      this._ws.onmessage = (rawEvent) => {
        if (this._ws !== ws) return;
        if (rawEvent.data && rawEvent.data.length > MAX_PAYLOAD_SIZE) return;
        try {
          this._handleServerEvent(JSON.parse(rawEvent.data));
        } catch {
          // Malformed JSON — ignore
        }
      };

      this._ws.onclose = (event) => {
        _log('WS closed:', event.code, event.reason, 'wasClean:', event.wasClean);
        // Ignore closes from orphaned/superseded sockets. Only the CURRENT
        // socket's lifecycle may stop the heartbeat or drive reconnection —
        // otherwise a self-closing orphan kills the live heartbeat and spawns
        // spurious reconnects (connection churn).
        if (this._ws !== ws) return;

        this._connected = false;
        this._subscribed = false;
        this._authenticated = false;
        this._ws = null;
        this._stopHeartbeat();

        // Auth failure (4001 / 4003 / AUTH_FAILED) → try token refresh first
        if (event.code === 4001 || event.code === 4003 || this._authRejected) {
          _log('WS auth failed — attempting token refresh before fallback');
          this._authRejected = false; // reset for next attempt
          this._handleWsAuthFailure();
          return;
        }

        // Normal close / network error → reconnect with backoff
        if (!this._isDestroyed) {
          this._scheduleReconnect();
        }
      };

      this._ws.onerror = () => {
        // onclose fires after onerror — reconnect handled there
      };
    } catch (err) {
      this._isConnecting = false;
      console.error('[RT] WebSocket error:', err);
      this._switchToPollingOnly();
    }
  }

  /**
   * Handle WS authentication failure by attempting a token refresh.
   * If refresh succeeds → reconnect WS with fresh token.
   * If refresh fails → fall back to polling-only mode.
   * This prevents permanent polling fallback when the token simply expired.
   */
  async _handleWsAuthFailure() {
    if (this._isDestroyed) return;

    try {
      _log('Refreshing token for WS reconnect...');
      const refreshed = await requestTokenRefresh();

      if (refreshed && !this._isDestroyed) {
        _log('Token refreshed — reconnecting WS');
        // Reset reconnect counter since this is an auth-driven reconnect
        this._reconnectAttempts = 0;
        this._connectWebSocket();
        return;
      }
    } catch (err) {
      console.warn('[RT] Token refresh for WS failed:', err.message);
    }

    // Refresh failed — fall back to polling
    _log('Token refresh failed — switching to polling');
    this._switchToPollingOnly();
  }

  /**
   * Permanently switch to polling-only mode.
   * Stops all WS reconnect attempts and starts a single polling timer.
   * Called when WS auth fails AND token refresh also fails.
   */
  _switchToPollingOnly() {
    // Stop any pending reconnect timer
    clearTimeout(this._reconnectTimer);
    this._reconnectTimer = null;

    // Close WS if still open
    if (this._ws) {
      this._ws.onclose = null;
      try { this._ws.close(); } catch {}
      this._ws = null;
    }

    this._mode = 'polling';
    this._reconnectAttempts = 0;
    this._startPolling();
  }

  _handleServerEvent(data) {
    if (!data || !data.type) return;
    _log('WS event:', data.type, data.payload ? Object.keys(data.payload) : '(no payload)');

    // ── Auth handshake ──
    if (data.type === 'AUTH_OK') {
      this._authenticated = true;
      _log('AUTH_OK — subscribing to', this._tenantId);
      this._ws?.send(JSON.stringify({ action: 'subscribe', tenantId: this._tenantId }));
      // Do NOT emit CONNECTED yet — wait for SUBSCRIPTION_CONFIRMED
      return;
    }

    if (data.type === 'AUTH_FAILED') {
      console.warn('[RT] WS auth rejected:', data.reason);
      this._authRejected = true;
      this._ws?.close(4003, 'Auth failed');
      return;
    }

    // ── Subscription confirmed — NOW we're truly connected ──
    if (data.type === 'SUBSCRIPTION_CONFIRMED') {
      this._subscribed = true;
      this._connected = true;
      this._subscribedAt = Date.now();
      _log('SUBSCRIPTION_CONFIRMED — WS fully live ✓');
      this._startHeartbeat();
      eventBus.emit({
        type: REALTIME_EVENTS.CONNECTED,
        payload: { mode: 'ws' },
        tenantId: this._tenantId,
        source: 'local',
        timestamp: Date.now(),
      });
      // Reconcile on (re)connect — respects 10s cooldown
      this._doReconcile();
      return;
    }

    // ── Heartbeat pong ──
    if (data.type === 'pong') {
      clearTimeout(this._pongTimer);
      this._pongTimer = null;
      return;
    }

    // Reject events before subscription is confirmed
    if (!this._subscribed) {
      _log('Event before subscription — ignored:', data.type);
      return;
    }

    // Security: event type allowlist
    if (!ALLOWED_REMOTE_EVENT_TYPES.has(data.type)) {
      console.warn('[RT] Unknown event type rejected:', data.type);
      return;
    }

    // Security: tenant scope check
    if (data.tenantId && data.tenantId !== this._tenantId) {
      console.warn('[RT] Tenant mismatch — event rejected');
      return;
    }

    // Security: payload validation
    if (data.payload && typeof data.payload !== 'object') return;

    // Security: timestamp freshness (5 min max)
    if (data.timestamp) {
      const age = Math.abs(Date.now() - data.timestamp);
      if (age > 5 * 60 * 1000) {
        console.warn('[RT] Stale event rejected (age:', age, 'ms)');
        return;
      }
    }

    // ABDM Scan & Share (R58): the flat message carries ONLY { scan_id } — no personal data.
    // Passed through as the event payload; consumers refetch / reveal by scan_id.
    if (data.type === 'abha_scan_share' || data.type === 'abha_scan_share_claimed') {
      eventBus.emit({
        type: data.type,
        payload: { scan_id: data.scan_id },
        tenantId:  data.tenantId,
        source:    'remote',
        timestamp: Date.now(),
        eventId:   data.scan_id ? `${data.type}:${data.scan_id}` : undefined,
      });
      return;
    }

    eventBus.emit({
      type: data.type,
      payload: data.payload || {},
      tenantId: data.tenantId,
      source: 'remote',
      timestamp: data.timestamp || Date.now(),
      eventId: data.eventId,
      causedBy: data.causedBy,
    });
  }

  // ── Heartbeat (detect dead WS connections) ─────────────────────────

  _startHeartbeat() {
    this._stopHeartbeat();
    this._heartbeatTimer = setInterval(() => {
      if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return;

      this._ws.send(JSON.stringify({ action: 'ping' }));
      _log('Heartbeat ping →');

      // If no pong within timeout, connection is dead — force reconnect
      this._pongTimer = setTimeout(() => {
        console.warn('[RT] Heartbeat timeout — connection dead, reconnecting');
        try { this._ws?.close(4000, 'Heartbeat timeout'); } catch {}
      }, HEARTBEAT_TIMEOUT);
    }, HEARTBEAT_INTERVAL);
  }

  _stopHeartbeat() {
    clearInterval(this._heartbeatTimer);
    this._heartbeatTimer = null;
    clearTimeout(this._pongTimer);
    this._pongTimer = null;
  }

  // ═══════════════════════════════════════════════════════════════════
  // Polling Transport (ONLY when no WS_URL is configured)
  // ═══════════════════════════════════════════════════════════════════

  _startPolling() {
    if (this._isDestroyed) return;

    // Guard: prevent multiple polling timers from stacking
    // (can happen during rapid WS fail → fallback cycles)
    clearInterval(this._reconcileTimer);
    this._reconcileTimer = null;

    // Only emit CONNECTED once — prevent listener churn
    if (!this._connected) {
      this._connected = true;
      _log('Polling started (', POLL_INTERVAL / 1000, 's interval)');
      eventBus.emit({
        type: REALTIME_EVENTS.CONNECTED,
        payload: { mode: 'polling' },
        tenantId: this._tenantId,
        source: 'local',
        timestamp: Date.now(),
      });

      // Immediate first fetch only on initial connect
      this._doReconcile();
    } else {
      _log('Polling restarted (', POLL_INTERVAL / 1000, 's interval, already connected)');
    }

    // Regular interval
    this._reconcileTimer = setInterval(() => {
      if (this._isDestroyed) { clearInterval(this._reconcileTimer); return; }
      if (!crossTabSync.isLeader) return;
      this._doReconcile();
    }, POLL_INTERVAL);
  }

  // ═══════════════════════════════════════════════════════════════════
  // Reconciliation (60s safety-net alongside WS)
  // ═══════════════════════════════════════════════════════════════════

  _startReconciliation() {
    clearInterval(this._reconcileTimer);
    _log('Reconciliation started (', RECONCILE_INTERVAL / 1000, 's interval)');

    this._reconcileTimer = setInterval(() => {
      if (this._isDestroyed) { clearInterval(this._reconcileTimer); return; }
      if (!crossTabSync.isLeader) return;
      _log('Reconciliation poll...');
      this._doReconcile();
    }, RECONCILE_INTERVAL);
  }

  /**
   * Fetch server data and emit deltas.
   * Used by both polling-only mode and 60s WS reconciliation.
   *
   * Has a 10s cooldown to prevent API hammering during rapid
   * reconnect cycles or multiple callers triggering at once.
   */
  async _doReconcile() {
    if (this._isDestroyed || !crossTabSync.isLeader) return;

    // ── 10s cooldown — prevents 429 during reconnect storms ──
    const now = Date.now();
    if (now - this._lastReconcileAt < 10_000) {
      _log('Reconcile skipped — cooldown (',
        Math.round((10_000 - (now - this._lastReconcileAt)) / 1000), 's left)');
      return;
    }

    // If a token refresh is in-flight, wait for it before polling
    const pendingRefresh = getRefreshPromise();
    if (pendingRefresh) {
      _log('Reconcile waiting for token refresh...');
      try {
        await pendingRefresh;
      } catch {
        // Refresh failed — proceed anyway, apiClient interceptor will handle 401
      }
    }

    this._lastReconcileAt = Date.now();

    try {
      if (!this._pollFetcher) {
        _log('Reconcile skipped — no pollFetcher');
        return;
      }
      _log('Reconcile fetching...');
      const data = await this._pollFetcher();
      if (this._isDestroyed) return;
      if (data) {
        const visitCount = data.dailyVisits?.length ?? 0;
        _log('Reconcile fetched:', visitCount, 'visits,',
          data.doctors?.length ?? 0, 'doctors');
        this._diffAndEmit(data);
      } else {
        _log('Reconcile fetch returned null (API error?)');
      }
    } catch (err) {
      // Don't log 401 as an error — it's handled by the interceptor/refresh
      if (err?.response?.status === 401) {
        _log('Reconcile got 401 — refresh in progress, will retry next cycle');
      } else {
        console.warn('[RT] Reconcile error:', err.message);
      }
    }
  }

  /**
   * Compare fresh server data with previous snapshot.
   * Emit granular events for each detected change.
   */
  _diffAndEmit(serverData) {
    const { dailyVisits: newVisits, doctors: newDoctors } = serverData;

    // ── Doctor availability diff ────────────────────────────────
    if (newDoctors) {
      const prevDoctors = this._previousDoctors;
      this._previousDoctors = newDoctors;

      if (prevDoctors) {
        const prevDocMap = new Map(prevDoctors.map((d) => [d.member_id || d.doctor_id, d]));
        newDoctors.forEach((doc) => {
          const docId = doc.member_id || doc.doctor_id;
          const old = prevDocMap.get(docId);
          if (old && old.availability !== doc.availability) {
            eventBus.emit({
              type: REALTIME_EVENTS.DOCTOR_STATUS_CHANGED,
              payload: {
                memberId: docId,
                availability: doc.availability,
                doctorName: doc.name || `${doc.first_name || ''} ${doc.last_name || ''}`.trim(),
              },
              tenantId: this._tenantId,
              source: 'remote',
              timestamp: Date.now(),
              eventId: `recon_doc_${docId}_${doc.availability}_${Date.now()}`,
            });
          }
        });
      }
    }

    if (!newVisits) return;

    const prev = this._previousDailyVisits;
    this._previousDailyVisits = newVisits;

    // First poll — no diff possible, just set baseline
    if (!prev) return;

    // Build lookup maps
    const prevMap = new Map(prev.map((v) => [v.id || v.visit_id, v]));
    const newMap = new Map(newVisits.map((v) => [v.id || v.visit_id, v]));

    const events = [];

    // Detect new visits
    newMap.forEach((visit, id) => {
      if (!prevMap.has(id)) {
        events.push({
          type: REALTIME_EVENTS.VISIT_CREATED,
          payload: { visit },
          tenantId: this._tenantId,
          source: 'remote',
          timestamp: Date.now(),
          eventId: `recon_create_${id}_${Date.now()}`,
        });
      }
    });

    // Detect updated visits
    newMap.forEach((visit, id) => {
      const old = prevMap.get(id);
      if (!old) return;

      // Status change
      if (old.status !== visit.status) {
        events.push({
          type: REALTIME_EVENTS.VISIT_STATUS_CHANGED,
          payload: {
            visitId: id,
            previousStatus: old.status,
            newStatus: visit.status,
            visit,
          },
          tenantId: this._tenantId,
          source: 'remote',
          timestamp: Date.now(),
          eventId: `recon_status_${id}_${visit.status}_${Date.now()}`,
        });
      } else if (JSON.stringify(old) !== JSON.stringify(visit)) {
        // Other field changes
        events.push({
          type: REALTIME_EVENTS.VISIT_UPDATED,
          payload: { visitId: id, visit },
          tenantId: this._tenantId,
          source: 'remote',
          timestamp: Date.now(),
          eventId: `recon_update_${id}_${Date.now()}`,
        });
      }
    });

    // Detect deleted visits
    prevMap.forEach((_, id) => {
      if (!newMap.has(id)) {
        events.push({
          type: REALTIME_EVENTS.VISIT_DELETED,
          payload: { visitId: id },
          tenantId: this._tenantId,
          source: 'remote',
          timestamp: Date.now(),
          eventId: `recon_delete_${id}_${Date.now()}`,
        });
      }
    });

    // Emit detected changes (if any)
    if (events.length > 0) {
      _log('Reconcile diff →', events.length, 'changes:', events.map(e => e.type));
      eventBus.emitBatch(events);

      // ── Cross-tab sync: send a SINGLE state snapshot to non-leader tabs ──
      // Individual recon_* events are blocked from cross-tab relay (storm fix),
      // so non-leader tabs need the updated state via snapshot instead.
      // This is ONE message vs N events — no storm risk.
      try {
        const snapshotData = { dailyVisits: newVisits };
        if (newDoctors) snapshotData.doctors = newDoctors;
        crossTabSync.broadcastSnapshot('visit', snapshotData);
        _log('Reconcile snapshot broadcast → non-leader tabs');
      } catch (snapErr) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[RT] Snapshot broadcast error:', snapErr.message);
        }
      }
    } else {
      _log('Reconcile diff → no changes detected');
    }
  }

  // ── Visibility Change ──────────────────────────────────────────────
  _onVisibilityChange() {
    if (!document.hidden && crossTabSync.isLeader) {
      _log('Tab visible — immediate reconcile');
      this._doReconcile();
    }
  }

  // ── Reconnect (WebSocket) ─────────────────────────────────────────
  _scheduleReconnect() {
    if (this._isDestroyed) return;

    // Dedup — never stack reconnect timers. Multiple onclose events (or an
    // auth-failure path racing a normal close) would otherwise queue several
    // timers, each firing _connectWebSocket and churning connections.
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }

    // Only reset backoff if previous connection was STABLE (>30s).
    // Prevents rapid connect-drop cycles from resetting to 1s forever.
    const wasStable = this._subscribedAt > 0 &&
      (Date.now() - this._subscribedAt > 30_000);
    if (wasStable) {
      _log('Previous connection was stable — resetting backoff');
      this._reconnectAttempts = 0;
    }
    this._subscribedAt = 0; // clear for next connection

    if (this._reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.warn('[RT] WS retries exhausted → falling back to polling-only');
      this._mode = 'polling';
      this._startPolling();
      return;
    }

    this._reconnectAttempts++;
    const delay = Math.min(
      RECONNECT_BASE_DELAY * Math.pow(2, this._reconnectAttempts - 1),
      RECONNECT_MAX_DELAY
    );

    _log('Reconnecting in', delay, 'ms (attempt', this._reconnectAttempts, ')');

    eventBus.emit({
      type: REALTIME_EVENTS.RECONNECTING,
      payload: { attempt: this._reconnectAttempts, delay },
      tenantId: this._tenantId,
      source: 'local',
      timestamp: Date.now(),
    });

    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      if (!this._isDestroyed) {
        this._connectWebSocket();
      }
    }, delay);
  }
}

// Singleton
const realtimeSyncService = new RealtimeSyncService();

// ── Global diagnostic — call window.__akRealtimeDiag() in browser console ──
if (typeof window !== 'undefined') {
  window.__akRealtimeDiag = () => ({
    mode: realtimeSyncService._mode,
    connected: realtimeSyncService._connected,
    subscribed: realtimeSyncService._subscribed,
    authenticated: realtimeSyncService._authenticated,
    tenantId: realtimeSyncService._tenantId ? '✓ set' : '✗ null',
    wsReadyState: ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][realtimeSyncService._ws?.readyState] ?? 'no ws',
    reconnectAttempts: realtimeSyncService._reconnectAttempts,
    heartbeatActive: !!realtimeSyncService._heartbeatTimer,
    reconcileActive: !!realtimeSyncService._reconcileTimer,
    lastReconcileSecsAgo: realtimeSyncService._lastReconcileAt
      ? Math.round((Date.now() - realtimeSyncService._lastReconcileAt) / 1000) + 's'
      : 'never',
    subscribedSecsAgo: realtimeSyncService._subscribedAt
      ? Math.round((Date.now() - realtimeSyncService._subscribedAt) / 1000) + 's'
      : 'not subscribed',
    baselineVisits: realtimeSyncService._previousDailyVisits?.length ?? 'not set',
    eventBusListeners: eventBus.listenerCount(),
    crossTabLeader: crossTabSync.isLeader,
  });
}

export default realtimeSyncService;
